import React, { forwardRef } from 'react'
import InputMask from 'sb-text-mask'
import { useUniqueId } from 'sb-hooks'
import PropTypes from 'prop-types'
import cx from 'classnames'
import { intlMessageShape } from 'sb-react-intl'

import Message from 'components/text/Message/Message'
import CircleLoader from 'components/loaders/CircleLoader/CircleLoader'
import Icon from 'components/_new/ui/Icon/Icon'
import FieldError from 'components/_new/forms/FieldError/FieldError'

import s from './UnderlineInputView.scss'

import eraseImage from './images/erase.svg'


const UnderlineInputView = forwardRef((props, ref) => {
  const {
    className, label, type, value, error,
    icon, iconColor,
    mask, maskChar, maskGuide,
    readOnly, errored, loading, withEraser,
    beforeNode, afterNode,
    onChange, onBlur, onFocus, onErase, onIconClick, onTouchStart, onBeforeNodeClick, onAfterNodeClick,
    dataTestId, id,
    blackOnFocus, // test for /subscribe-prototype
    iconButtonProps,
    autoComplete,
    eraserLabel,
  } = props

  const uniqueId = useUniqueId()
  const inputId = id || `input_${uniqueId}` // for "htmlFor" attribute

  const rootClassName = cx(s.root, {
    [s.errored]: errored || error,
  })

  const inputClassName = cx(s.input, className, {
    [s.filled]: Boolean(value),
    [s.disabled]: readOnly,
    [s.blackOnFocus]: blackOnFocus,
  })

  const nodeProps = {
    className: inputClassName,
    id: inputId,
    type,
    value,
    placeholder: '',
    readOnly,
    disabled: readOnly,
    tabIndex: readOnly ? -1 : 0,
    autoComplete,
    onChange,
    onBlur,
    onFocus,
    onTouchStart,
    'data-testid': dataTestId,
  }

  if (mask) {
    nodeProps.inputRef = ref
    nodeProps.mask = mask
    nodeProps.guide = maskGuide
    nodeProps.placeholderChar = maskChar
  }
  else {
    nodeProps.ref = ref
  }

  const inputNode = React.createElement(mask ? InputMask : 'input', nodeProps)

  return (
    <div className={rootClassName}>
      <div className={s.content}>
        {
          Boolean(beforeNode) && (
            <div className={s.beforeContainer} onClick={onBeforeNodeClick}>
              {beforeNode}
            </div>
          )
        }
        <div className={s.inputContainer}>
          {inputNode}
          <div className={s.underline} />
          <Message className={s.label} text={label} tag="label" htmlFor={inputId} />
        </div>
        {
          Boolean(!loading && withEraser && value) && (
            <button className={s.eraser} type="button" onClick={onErase} aria-label={eraserLabel}>
              <img src={eraseImage} alt="" />
            </button>
          )
        }
        {
          Boolean(!loading && icon) && (
            <button type="button" onClick={onIconClick} {...iconButtonProps}>
              <Icon className={s.icon} name={icon} color={iconColor} />
            </button>
          )
        }
        {
          Boolean(loading) && (
            <CircleLoader />
          )
        }
        {
          Boolean(afterNode) && (
            <div className={s.afterContainer} onClick={onAfterNodeClick}>
              {afterNode}
            </div>
          )
        }
      </div>
      {
        Boolean(error) && (
          <FieldError className={s.error} message={error} />
        )
      }
    </div>
  )
})

UnderlineInputView.propTypes = {
  className: PropTypes.string,
  type: PropTypes.string,
  labelId: PropTypes.string,
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape(intlMessageShape),
  ]),
  value: PropTypes.string,
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape(intlMessageShape),
  ]),
  icon: PropTypes.string,
  iconColor: PropTypes.string,
  mask: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
  ]),
  maskChar: PropTypes.string,
  maskGuide: PropTypes.bool,
  readOnly: PropTypes.bool,
  loading: PropTypes.bool,
  withEraser: PropTypes.bool,
  beforeNode: PropTypes.node,
  afterNode: PropTypes.node,
  onChange: PropTypes.func.isRequired,
  onErase: PropTypes.func,
  onIconClick: PropTypes.func,
  onBeforeNodeClick: PropTypes.func,
  onAfterNodeClick: PropTypes.func,
  dataTestId: PropTypes.string,
  id: PropTypes.string,
  iconButtonProps: PropTypes.object,
}

UnderlineInputView.defaultProps = {
  maskGuide: false,
  iconButtonProps: {},
}


export default UnderlineInputView
